<template>
  <div class="space-y-6">
    <div class="flex justify-between items-center">
      <h2 class="text-2xl font-bold text-gray-800">Company Benefits</h2>
      <button
        class="bg-brand-black p-3 rounded-xl text-white cursor-pointer disabled:bg-opacity-10 disabled:cursor-not-allowed"
        @click="handleSave"
        :disabled="!canSave || benefits.length === 0"
      >
        Save Changes
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card v-for="(benefit, index) in benefits" :key="index" class="relative group" v-if="benefits.length >= 1">
        <CardHeader class="pb-2">
          <CardTitle class="text-lg flex items-center">
            <span class="text-2xl mr-2">{{ benefit.icon }}</span>
            {{ benefit.title }}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p class="text-sm text-gray-600">{{ benefit.description }}</p>
        </CardContent>
        <CardFooter class="pt-0">
          <Button
            variant="destructive"
            size="sm"
            class="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2"
            @click="handleRemoveBenefit(index)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </CardFooter>
      </Card>

      <div v-else class="col-span-3 text-center font-semibold text-xl border py-5 rounded-md">
        <p>Add company benefits</p>
      </div>
    </div>

    <Card class="mt-6">
      <CardHeader>
        <CardTitle>Add New Benefit</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label for="title">Benefit Title</Label>
            <Input id="title" v-model="newBenefit.title" placeholder="e.g., Health Insurance" />
          </div>

          <div>
            <Label for="icon">Icon (Emoji)</Label>
            <Input id="icon" v-model="newBenefit.icon" placeholder="e.g., 🏥" />
          </div>

          <div>
            <Label for="description">Description</Label>
            <Input id="description" v-model="newBenefit.description" placeholder="Brief description of the benefit" />
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <button
          @click="handleAddBenefit"
          class="bg-brand-black p-3 rounded-xl text-white cursor-pointer flex items-center disabled:bg-opacity-10 disabled:cursor-not-allowed"
          :disabled="isDisabled"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-2"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add Benefit
        </button>
        <small class="text-sm font-semibold mt-4 text-red-500">* Save changes before you continue</small>
      </CardFooter>
    </Card>

    <Loading :isLoading="isLoading" className="fixed inset-0 bg-[#1B224B]/[.2] flex justify-center items-center" />
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, onMounted } from 'vue';
import Button from '../ui/Button.vue';
import Input from '../ui/Input.vue';
import Label from '../ui/Label.vue';
import Card from '../ui/Card.vue';
import CardHeader from '../ui/CardHeader.vue';
import CardTitle from '../ui/CardTitle.vue';
import CardContent from '../ui/CardContent.vue';
import CardFooter from '../ui/CardFooter.vue';
import { useStore } from 'vuex';
import Loading from '@/components/Loading.vue';

const store = useStore();
const props = defineProps({ companyDetails: { type: Object } });
const emit = defineEmits(['refreshCompanyProfile']);


const benefits = ref([]);
const newBenefits = ref([]); // Only track newly added benefits
const newBenefit = reactive({
  title: '',
  icon: '',
  description: '',
});
const canSave = ref(false);
const isLoading = ref(false);

const isDisabled = computed(() => {
  return !(newBenefit.title && newBenefit.icon && newBenefit.description);
});

const handleAddBenefit = () => {
  const benefit = { ...newBenefit };
  benefits.value.push(benefit);
  newBenefits.value.push(benefit); // Track this addition for submission

  newBenefit.title = '';
  newBenefit.icon = '';
  newBenefit.description = '';
};

const handleRemoveBenefit = (id) => {
  benefits.value = benefits.value.filter((_, index) => index !== id);
};

const handleSave = async () => {
  if (newBenefits.value.length === 0) return;

  isLoading.value = true;
  try {
    await store.dispatch('auth/updateUserProfile', { benefits: newBenefits.value });
    newBenefits.value = []; // Clear after successful save
    canSave.value = false;
    emit('refreshCompanyProfile');
  } catch (error) {
    console.error('Error saving new benefits:', error);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => newBenefits.value.length,
  (newLength, oldLength) => {
    if (newLength !== oldLength) {
      canSave.value = true;
    }
  },
);

onMounted(() => {
  isLoading.value = true;
  if (props.companyDetails && Array.isArray(props.companyDetails.benefits)) {
    benefits.value = [...props.companyDetails.benefits];
  }
  isLoading.value = false;
});
</script>

